import {
  Box,
  Column,
  FlexBox,
  Row,
  Span,
  Typography,
  useScreenClass,
  VisuallyHidden,
} from '@vp/swan';
import { ModTarget } from '@vp/mod-father';
import { useTranslations } from '@vp/om-translations';
import {
  ApplicationName,
  FulfillmentStatusBanner,
  type FulfillmentStatusBannerProps,
} from '@vp/fulfillment-history';
import {
  mapItemStatesToStatuses,
  shouldShowPromisedArrivalDate,
} from '../../utils/orderUtils';
// import LineItemStatus from './LineItemStatus';
import { useLineItemData } from '../contexts/LineItemDataContext';
import { useLineItemActions } from '../contexts/LineItemActionsContext';
import { LineItemActions } from './LineItemActions';
import { useSiteData } from '../../hooks/useSiteData';
import { useShopperId } from '../../hooks/useShopperId';
import { useOrderSummary } from '../contexts/OrderSummaryContext';
import { ORDER_HISTORY_SOURCE_PAGE } from '../../constants/sourcePage';
import { ExpectedDeliveryMessage } from './ExpectedDeliveryMessage';
import { LineItemStatus } from './LineItemStatus';
import { ItemPreviewDisplay } from '../shared/itemPreviewDisplay/ItemPreviewDisplay';
import { mapInternalItemDataToFulfillmentHistoryLineItemDetails } from '../../utils/fulfillmentHistoryUtils';

/**
 * Displays product preview image, product name, status and quantity.
 * If an item has a fulfillment history, the status and quantity are replaced by the FulfillmentStatusBanner.
 * This component also displays LineItemActions.
 */
export const ProductDetailsDisplay = () => {
  const { locale, tenantEnvironment } = useSiteData();
  const shopperId = useShopperId();
  const {
    order: { orderNumber },
  } = useOrderSummary();

  const [lineItemMessages = {}] = useTranslations(['lineItemMessages']);

  const screenClass = useScreenClass();
  const isMobile = ['xs', 'sm'].includes(screenClass);

  const { fulfillmentHistoryEnabledForLineItem } = useLineItemActions();

  const item = useLineItemData();

  const {
    cmrd: promisedArrivalDate, // fall back to this if we do not have expected delivery date from fulfillment history
    deliverability: { itemDeliverable: isDeliverable },
    fulfillmentHistory,
    id,
    quantity,
    states: lineItemStates,
    productName = '',
  } = item;

  if (!id) {
    return null;
  }

  const shouldShowFulfillmentHistory =
    fulfillmentHistoryEnabledForLineItem &&
    !!fulfillmentHistory?.fulfillments &&
    fulfillmentHistory.fulfillments.length > 0;

  const lineItemStatuses = mapItemStatesToStatuses(
    lineItemStates || [],
    isDeliverable
  );

  const showExpectedDelivery =
    promisedArrivalDate &&
    shouldShowPromisedArrivalDate(
      promisedArrivalDate,
      lineItemStatuses,
      true,
      shouldShowFulfillmentHistory
    );

  const singleFulfillment = fulfillmentHistory?.fulfillments?.[0];

  const expectedDeliveryDate = shouldShowFulfillmentHistory
    ? singleFulfillment?.expectedDeliveryDate || ''
    : promisedArrivalDate || '';

  // On the Order History page, these components display in different places depending on desktop vs. mobile views.
  // We don't need similar treatment for Order Details/ confirmation, because their versions of these
  // components always display below the entire content of this component, so we can display them in DetailsLineItemDisplay.
  const orderHistoryModTargetAndLineItemActions = (
    <FlexBox flexDirection="column" gap="between-actions">
      {/* mod targets rendered on the Order History page have access to the contracts defined in HistoryItemUXModWrapper */}
      <div className="noprint">
        <ModTarget name="itemHistoryDetails" />
      </div>
      <div data-testid="line-item-actions-for-order-history-desktop">
        <LineItemActions />
      </div>
      <div className="noprint">
        <ModTarget name="belowOrderHistoryItemCTAs" />
      </div>
    </FlexBox>
  );

  const fulfillmentStatusBannerProps: FulfillmentStatusBannerProps = {
    applicationName: ApplicationName.ORDER_HISTORY,
    debug: process.env.NODE_ENV !== 'production',
    lineItemFulfillmentHistory: fulfillmentHistory!,
    lineItemDetails:
      mapInternalItemDataToFulfillmentHistoryLineItemDetails(item),
    locale: locale,
    orderNumber: orderNumber,
    shopperId: shopperId,
    sourcePage: ORDER_HISTORY_SOURCE_PAGE,
    tenantEnvironment:
      tenantEnvironment === 'PRODUCTION' ? 'PRODUCTION' : 'STAGING',
  };

  return (
    <>
      <Row>
        {/* Columns are in reverse order for better readibility for screen readers */}
        {/* Column 2: Visual product name, quantity and status */}
        <Column span={8} spanXs={8} push={4}>
          {/* Assistive technology (for non-sighted users) expects all info described by a heading to follow it.
              This product name is hidden from visual users to prevent redundancy since we also render
              a visible product name next to the product image */}
          <span id="product-title">
            <VisuallyHidden
              id={`product-name-${productName}`}
              data-testid="visually-hidden-product-name"
            >
              {productName}
            </VisuallyHidden>
          </span>

          {/* This product name is intended for those viewing the visual UI
            and is hidden from assistive tech to prevent redundancy
            since we also render a visually hidden product name above the product image */}
          <Typography
            fontWeight="bold"
            fontSize="standard"
            aria-hidden
            data-testid="aria-hidden-product-name"
          >
            {productName}
          </Typography>

          {/* quantity */}
          <div id="item-quantity" data-testid="item-quantity">
            <Span textColor="subtle">
              {`${lineItemMessages.quantityField}: `} {quantity}
            </Span>
          </div>

          {!shouldShowFulfillmentHistory ? (
            <>
              <LineItemStatus statuses={lineItemStatuses} />
              {showExpectedDelivery ? (
                <ExpectedDeliveryMessage
                  expectedDeliveryDate={expectedDeliveryDate}
                  textColorOverride="subtle"
                />
              ) : null}
            </>
          ) : null}
          {/* for larger screen sizes, show fulfillment status banner, and orderHistory-specific content here */}
          {!isMobile ? (
            <Box
              marginY={5}
              data-testid="product-details-display-content-for-larger-screens"
            >
              {shouldShowFulfillmentHistory ? (
                <Box
                  data-testid="fulfillment-status-banner-for-larger-screens"
                  mb="between-actions"
                >
                  <FulfillmentStatusBanner {...fulfillmentStatusBannerProps} />
                </Box>
              ) : null}
              <Box
                data-testid="product-details-display-order-history-content-for-larger-screens"
                my="between-actions"
              >
                {orderHistoryModTargetAndLineItemActions}
              </Box>
            </Box>
          ) : null}
        </Column>

        {/* Column 1: product image and product name for screen reader */}
        <Column span={4} spanXs={4} pull={8} pr={7}>
          <div
            id="product-preview"
            aria-labelledby="product-title item-quantity"
          >
            <ItemPreviewDisplay />
          </div>
        </Column>
      </Row>

      {/* for smaller screen sizes, display the fulfillment status as a full-width banner beneath image and quantity, plus orderHistory-specific content */}
      {isMobile ? (
        <Row data-test-id="product-details-display-content-for-smaller-screens">
          <Column span={12} my="between-subsections">
            {shouldShowFulfillmentHistory ? (
              <Box
                my="between-subsections"
                data-testid="fulfillment-status-banner-for-smaller-screens"
              >
                <FulfillmentStatusBanner {...fulfillmentStatusBannerProps} />
              </Box>
            ) : null}
            <Box
              data-testid="product-details-display-order-history-content-for-smaller-screens"
              my="between-subsections"
            >
              {orderHistoryModTargetAndLineItemActions}
            </Box>
          </Column>
        </Row>
      ) : null}
    </>
  );
};
